import { Dropdown } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

import logoLight from "assets/logo-docttorpay-horizontal.svg";
import { useAuth } from "providers/Auth";
import { useDarkMode } from "providers/DarkMode";
import { useUser } from "providers/User";
import { RoleDescription } from "types/enums";

import NotificationAlert from "./components/NotificationAlert";
import OffCanvasMenu from "./components/OffCanvasMenu";
import * as S from "./styles";

const NAV_ITEMS = (userRole: string) => [
  {
    label: "Orçamentos",
    to:
      userRole === RoleDescription.Patient
        ? "/orcamentos-paciente"
        : "/orcamentos-profissional",
    match: ["/orcamentos-profissional", "/orcamentos-paciente"],
    roles: "all",
  },
  {
    label: "Profissionais",
    to: "/profissionais",
    match: ["/profissionais"],
    roles: [RoleDescription.Admin],
  },
  {
    label: "Secretárias",
    to: "/secretarias",
    match: ["/secretarias"],
    roles: [RoleDescription.Admin],
  },
  {
    label: "Instituições",
    to: "/instituicoes",
    match: ["/instituicoes"],
    roles: [RoleDescription.Admin],
  },
  {
    label: "Pacientes",
    to: "/pacientes",
    match: ["/pacientes"],
    roles: [RoleDescription.Admin, RoleDescription.Professional, RoleDescription.Secretary],
  },
  {
    label: "Usuários",
    to: "/usuarios",
    match: ["/usuarios"],
    roles: [RoleDescription.Admin],
  },
  {
    label: "Calendário",
    to: "/agenda",
    match: ["/agenda"],
    roles: [RoleDescription.Admin, RoleDescription.Professional, RoleDescription.Secretary],
  },
  {
    label: "Relatórios",
    to: "/relatorios",
    match: ["/relatorios"],
    roles: "all",
  },
];

const getInitials = (name?: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Header = () => {
  const { token, userRole, logout, userContract } = useAuth();
  const { userData } = useUser();
  const { pathname } = useLocation();
  const { darkMode, toggleDarkMode } = useDarkMode();

  if (!token) return null;

  const visibleItems = NAV_ITEMS(userRole).filter((item) =>
    item.roles === "all" || (item.roles as string[]).includes(userRole)
  );

  const displayName = userData?.socialName || userData?.name || "";
  const initials = getInitials(displayName);

  const roleLabel = {
    [RoleDescription.Admin]: "Administrador",
    [RoleDescription.Professional]: "Profissional",
    [RoleDescription.Secretary]: "Secretária",
    [RoleDescription.Patient]: "Paciente",
  }[userRole] ?? userRole;

  return (
    <S.Nav aria-label="Navegação principal">
      <div className="container">
        <S.Inner>
          {/* Logo */}
          <S.LogoLink as={Link} to="/" aria-label="Ir para o início">
            <img src={logoLight} alt="DocttorPay" />
          </S.LogoLink>

          {/* Nav links — desktop */}
          <S.NavList
            role="menubar"
            aria-label="Menu de navegação"
            className={
              userRole === RoleDescription.Admin
                ? "d-none d-xxl-flex"
                : "d-none d-lg-flex"
            }
          >
            {visibleItems.map((item) => {
              const isActive = item.match.some((m) => pathname.startsWith(m));
              return (
                <S.NavItem key={item.to} role="none">
                  <S.NavLinkStyled
                    as={Link}
                    to={item.to}
                    $active={isActive}
                    role="menuitem"
                    aria-current={isActive ? "page" : undefined}
                  >
                    {item.label}
                  </S.NavLinkStyled>
                </S.NavItem>
              );
            })}
          </S.NavList>

          <S.Spacer />

          {/* Right section */}
          <S.RightSection>
            {/* Notifications */}
            <NotificationAlert />

            {/* Theme toggle */}
            <S.ThemeToggle
              $dark={darkMode}
              onClick={toggleDarkMode}
              aria-label={darkMode ? "Mudar para tema claro" : "Mudar para tema escuro"}
              title={darkMode ? "Tema claro" : "Tema escuro"}
            >
              <i
                className={darkMode ? "uil uil-sun" : "uil uil-moon"}
                aria-hidden="true"
              />
            </S.ThemeToggle>

            <S.Divider aria-hidden="true" />

            {/* User dropdown */}
            <Dropdown align="end">
              <Dropdown.Toggle as="div" bsPrefix="no-caret">
                <S.UserButton
                  aria-label={`Menu do usuário: ${displayName}`}
                  aria-haspopup="true"
                >
                  <S.Avatar aria-hidden="true">{initials}</S.Avatar>
                  <S.UserName>{displayName}</S.UserName>
                  <S.ChevronIcon className="uil uil-angle-down" aria-hidden="true" />
                </S.UserButton>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Header className="lh-1 pb-2">
                  <div className="fw-bold">{displayName}</div>
                  <div className="small text-muted">{userData?.email}</div>
                  <span
                    className="badge mt-1"
                    style={{
                      background: "rgba(var(--primary-rgb),0.1)",
                      color: "var(--primary)",
                      fontWeight: 600,
                      fontSize: "0.6875rem",
                    }}
                  >
                    {roleLabel}
                  </span>
                </Dropdown.Header>

                <Dropdown.Divider />

                <Dropdown.Item as={Link} to="/meus-dados">
                  <i className="uil uil-user me-2" aria-hidden="true" />
                  Meus Dados
                </Dropdown.Item>

                {!!userContract && (
                  <Dropdown.Item
                    as={Link}
                    to="/termos-e-contrato"
                  >
                    <i className="uil uil-file-contract me-2" aria-hidden="true" />
                    {userRole === RoleDescription.Professional
                      ? "Termos e Contratos"
                      : "Termos"}
                  </Dropdown.Item>
                )}

                {!userContract && userRole === RoleDescription.Professional && (
                  <Dropdown.Item as={Link} to="/contract">
                    <i className="uil uil-file-contract me-2" aria-hidden="true" />
                    Contrato
                  </Dropdown.Item>
                )}

                <Dropdown.Divider />

                <Dropdown.Item
                  as="a"
                  className="text-danger"
                  onClick={logout}
                  aria-label="Sair da conta"
                >
                  <i className="uil uil-sign-out-alt me-2" aria-hidden="true" />
                  Sair
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Mobile hamburger */}
            <OffCanvasMenu />
          </S.RightSection>
        </S.Inner>
      </div>
    </S.Nav>
  );
};

export default Header;
